/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * DSA Settings Component (User Settings)
 * 
 * User-level settings for participating in Distributed System Agents (DSA).
 * DSA enablement and repo configuration are managed at the organization level.
 * This component allows users to:
 * - Join/leave the DSA network (if allowed by org settings)
 * - Configure their local worker settings (poll interval, eligibility score)
 * - View worker status and connected agents
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Network, 
  Check, 
  RefreshCw, 
  Play, 
  Square, 
  GitBranch,
  Clock,
  Zap,
  AlertCircle,
  Building2,
  Lock
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  getDSAStatus,
  enableDSA,
  disableDSA,
  syncDSARepo,
  updateDSAWorkerConfig,
  type EnableDSAOptions,
} from '../../lib/api/dsa';
import { getOrganizationSettings } from '@shared/lib/api/organization_settings';
import type { DSAStatusResponse } from '@shared/schema/dsa';
import type { OrganizationIntegrationSettings, DSAOrgSettings } from '@shared/types/organizationSettings';

export function DSASettings() {
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<DSAStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // User participation state
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Worker config state
  const [pollIntervalMs, setPollIntervalMs] = useState(300000);
  const [minimumScore, setMinimumScore] = useState(0.6);
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);
  
  // Fetch organization settings to check org-level DSA configuration
  const { data: orgSettings, isLoading: isLoadingOrgSettings } = useQuery(getOrganizationSettings());
  
  // Extract org-level DSA settings
  const orgIntegrationSettings = orgSettings?.integrationSettings as OrganizationIntegrationSettings | undefined;
  const orgDSASettings: DSAOrgSettings | undefined = orgIntegrationSettings?.dsa;
  const isDSAEnabledOrgWide = orgDSASettings?.enabled ?? false;
  const allowUserParticipation = orgDSASettings?.allowUserParticipation ?? true;
  
  // Load status on mount
  useEffect(() => {
    loadStatus();
  }, []);
  
  const loadStatus = async () => {
    setLoading(true);
    setError(null);
    
    const result = await getDSAStatus();
    
    if (result.success && result.status) {
      setStatus(result.status);
      
      // Initialize worker config from status
      if (result.status.worker) {
        // We can't get the full config from status, but we can track local state
      }
    } else {
      setError(result.error || 'Failed to load DSA status');
    }
    
    setLoading(false);
  };
  
  const handleEnable = async () => {
    setIsEnabling(true);
    
    const options: EnableDSAOptions = {};
    
    // Use org-configured Velocity repo URL
    if (orgDSASettings?.velocityRepoUrl) {
      options.velocityRepoUrl = orgDSASettings.velocityRepoUrl;
    } else {
      toast({
        title: 'Repository not configured',
        description: 'Please ask your organization administrator to configure the Velocity repository.',
        variant: 'destructive',
      });
      setIsEnabling(false);
      return;
    }
    
    const result = await enableDSA(options);
    
    if (result.success) {
      toast({
        title: 'DSA Enabled',
        description: 'Your Velocity instance is now participating in DSA.',
      });
      loadStatus();
    } else {
      toast({
        title: 'Failed to enable DSA',
        description: result.error,
        variant: 'destructive',
      });
    }
    
    setIsEnabling(false);
  };
  
  const handleDisable = async () => {
    if (!confirm('Are you sure you want to disable DSA? The worker will stop polling.')) {
      return;
    }
    
    setIsDisabling(true);
    
    const result = await disableDSA();
    
    if (result.success) {
      toast({
        title: 'DSA Disabled',
        description: 'Distributed System Agents have been stopped.',
      });
      loadStatus();
    } else {
      toast({
        title: 'Failed to disable DSA',
        description: result.error,
        variant: 'destructive',
      });
    }
    
    setIsDisabling(false);
  };
  
  const handleSync = async () => {
    setIsSyncing(true);
    
    const result = await syncDSARepo();
    
    if (result.success) {
      toast({
        title: 'Sync Complete',
        description: 'Velocity repo has been synchronized.',
      });
      loadStatus();
    } else {
      toast({
        title: 'Sync Failed',
        description: result.error,
        variant: 'destructive',
      });
    }
    
    setIsSyncing(false);
  };
  
  const handleUpdateConfig = async () => {
    setIsUpdatingConfig(true);
    
    const result = await updateDSAWorkerConfig({
      pollIntervalMs,
      minimumScore,
    });
    
    if (result.success) {
      toast({
        title: 'Configuration Updated',
        description: 'Worker settings have been saved.',
      });
    } else {
      toast({
        title: 'Update Failed',
        description: result.error,
        variant: 'destructive',
      });
    }
    
    setIsUpdatingConfig(false);
  };
  
  if (loading || isLoadingOrgSettings) {
    return (
      <div style={{ 
        padding: '40px',
        textAlign: 'center',
        color: '#6b7280'
      }}>
        <RefreshCw size={24} className="spin" style={{ marginBottom: '12px' }} />
        <p>Loading DSA status...</p>
      </div>
    );
  }
  
  const isEnabled = status?.worker?.enabled ?? false;
  const workerStatus = status?.worker;
  
  // If DSA is disabled at the organization level, show appropriate message
  if (!isDSAEnabledOrgWide) {
    return (
      <div style={{ 
        padding: '20px',
        background: '#f9f9f9',
        borderRadius: '8px',
        border: '1px solid #ddd',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <Network size={20} style={{ marginRight: '8px', color: '#9ca3af' }} />
          <h3 style={{ margin: 0, color: '#6b7280' }}>Distributed System Agents (DSA)</h3>
        </div>
        
        <div style={{ 
          padding: '20px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <Lock size={32} style={{ color: '#9ca3af', marginBottom: '12px' }} />
          <h4 style={{ margin: '0 0 8px', color: '#374151' }}>DSA Not Available</h4>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 16px' }}>
            Distributed System Agents have not been enabled for your organization.
            Contact your organization administrator to enable DSA.
          </p>
          <div style={{ 
            padding: '12px',
            background: '#f3f4f6',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '13px',
            color: '#6b7280'
          }}>
            <Building2 size={16} />
            Organization-level setting controls DSA availability
          </div>
        </div>
      </div>
    );
  }
  
  // If DSA is enabled org-wide but user participation is not allowed
  // automatically enable DSA without user input
  const userCanControl = allowUserParticipation;
  
  return (
    <div style={{ 
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd',
      marginBottom: '32px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Network size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Distributed System Agents (DSA)</h3>
      </div>
      
      {/* Org-level DSA status banner */}
      <div style={{ 
        padding: '12px',
        background: '#f0fdf4',
        border: '1px solid #bbf7d0',
        borderRadius: '6px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        color: '#16a34a'
      }}>
        <Check size={16} />
        <span>DSA is enabled for your organization</span>
        {!userCanControl && (
          <span style={{ 
            marginLeft: 'auto', 
            padding: '2px 8px', 
            background: '#dcfce7', 
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            Auto-enabled by admin
          </span>
        )}
      </div>
      
      <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
        {userCanControl
          ? 'DSA enables your Velocity instance to participate in distributed agent coordination across your organization using a shared Git repository.'
          : 'Your organization administrator has enabled DSA for all users. Your Velocity instance participates in distributed agent coordination automatically.'}
      </p>
      
      {error && (
        <div style={{ 
          padding: '12px',
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      
      {/* Status Section */}
      {isEnabled && workerStatus && (
        <div style={{ 
          padding: '16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Worker Status</h4>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              background: '#dcfce7',
              color: '#16a34a',
              gap: '6px'
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a' }} />
              Active
            </span>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Worker ID</span>
              <p style={{ margin: '4px 0 0', fontFamily: 'monospace', fontSize: '13px' }}>
                {workerStatus.workerId}
              </p>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Last Poll</span>
              <p style={{ margin: '4px 0 0', fontSize: '13px' }}>
                {workerStatus.lastPoll 
                  ? new Date(workerStatus.lastPoll).toLocaleString()
                  : 'Not yet polled'}
              </p>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Runs Completed</span>
              <p style={{ margin: '4px 0 0', fontSize: '13px', fontWeight: 600 }}>
                {workerStatus.runsCompleted} total / {workerStatus.runsToday} today
              </p>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Repo Sync</span>
              <p style={{ 
                margin: '4px 0 0', 
                fontSize: '13px',
                color: workerStatus.repoStatus.synced ? '#16a34a' : '#dc2626'
              }}>
                {workerStatus.repoStatus.synced ? 'Synced' : 'Not synced'}
                {workerStatus.repoStatus.lastSync && (
                  <span style={{ color: '#6b7280', marginLeft: '8px' }}>
                    ({new Date(workerStatus.repoStatus.lastSync).toLocaleTimeString()})
                  </span>
                )}
              </p>
            </div>
          </div>
          
          {workerStatus.currentRun && (
            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              background: '#eff6ff',
              borderRadius: '4px',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <RefreshCw size={14} className="spin" style={{ color: '#2563eb' }} />
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#1d4ed8' }}>
                  Running: {workerStatus.currentRun.agentName}
                </span>
              </div>
              {workerStatus.currentRun.progress && (
                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#3b82f6' }}>
                  {workerStatus.currentRun.progress}
                </p>
              )}
            </div>
          )}
          
          {workerStatus.lastError && (
            <div style={{ 
              marginTop: '12px',
              padding: '8px 12px',
              background: '#fef2f2',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#dc2626'
            }}>
              Last error: {workerStatus.lastError}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              className="btn btn-secondary"
              onClick={handleSync}
              disabled={isSyncing}
              style={{ padding: '8px 16px', fontSize: '13px' }}
            >
              {isSyncing ? (
                <>
                  <RefreshCw size={14} className="spin" style={{ marginRight: '6px' }} />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw size={14} style={{ marginRight: '6px' }} />
                  Sync Now
                </>
              )}
            </button>
            
            {userCanControl && (
              <button
                className="btn btn-secondary"
                onClick={handleDisable}
                disabled={isDisabling}
                style={{ 
                  padding: '8px 16px', 
                  fontSize: '13px',
                  color: '#dc2626',
                  borderColor: '#fecaca',
                  background: '#fef2f2'
                }}
              >
                {isDisabling ? (
                  <>
                    <Square size={14} style={{ marginRight: '6px' }} />
                    Stopping...
                  </>
                ) : (
                  <>
                    <Square size={14} style={{ marginRight: '6px' }} />
                    Disable DSA
                  </>
                )}
              </button>
            )}
            {!userCanControl && (
              <span style={{ 
                padding: '8px 12px', 
                fontSize: '12px', 
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Lock size={12} />
                Managed by organization
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Enable Section - User participation when org has DSA enabled */}
      {!isEnabled && userCanControl && (
        <div style={{ 
          padding: '16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600 }}>
            Join DSA Network
          </h4>
          
          <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>
            Your organization has enabled DSA. Enable your local worker to participate 
            in running organization-wide agents on your machine.
          </p>
          
          {orgDSASettings?.velocityRepoUrl ? (
            <>
              <div style={{ 
                padding: '12px',
                background: '#f3f4f6',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <GitBranch size={14} style={{ color: '#6b7280' }} />
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>
                    Organization Velocity Repository
                  </span>
                </div>
                <code style={{ 
                  fontSize: '13px', 
                  color: '#374151',
                  background: '#e5e7eb',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  display: 'inline-block',
                  wordBreak: 'break-all'
                }}>
                  {orgDSASettings.velocityRepoUrl}
                </code>
              </div>
              
              <button
                className="btn"
                onClick={handleEnable}
                disabled={isEnabling}
                style={{ padding: '10px 20px' }}
              >
                {isEnabling ? (
                  <>
                    <RefreshCw size={16} className="spin" style={{ marginRight: '8px' }} />
                    Enabling...
                  </>
                ) : (
                  <>
                    <Play size={16} style={{ marginRight: '8px' }} />
                    Enable DSA Worker
                  </>
                )}
              </button>
            </>
          ) : (
            <div style={{ 
              padding: '12px',
              background: '#fefce8',
              border: '1px solid #fef08a',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#a16207',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={16} />
              <span>
                DSA is enabled for your organization, but the Velocity repository has not been 
                configured yet. Please ask your organization administrator to configure the 
                repository in Organization Settings.
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* Auto-enabled by organization notice */}
      {!isEnabled && !userCanControl && (
        <div style={{ 
          padding: '16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '12px',
            color: '#6b7280'
          }}>
            <Lock size={16} />
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>DSA Required by Organization</h4>
          </div>
          
          {orgDSASettings?.velocityRepoUrl ? (
            <>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>
                Your organization administrator has enabled DSA for all users. 
                DSA will connect using the organization's configured Velocity repository.
              </p>
              
              <div style={{ 
                padding: '12px',
                background: '#f3f4f6',
                borderRadius: '4px',
                fontSize: '13px'
              }}>
                <strong>Velocity Repo:</strong>{' '}
                <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>
                  {orgDSASettings.velocityRepoUrl}
                </code>
              </div>
              
              <button
                className="btn"
                onClick={handleEnable}
                disabled={isEnabling}
                style={{ marginTop: '16px', padding: '10px 20px' }}
              >
                {isEnabling ? (
                  <>
                    <RefreshCw size={16} className="spin" style={{ marginRight: '8px' }} />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Play size={16} style={{ marginRight: '8px' }} />
                    Connect to Organization DSA
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>
                Your organization administrator has enabled DSA for all users, but the Velocity repository 
                has not been configured yet. Please contact your administrator to complete the DSA setup.
              </p>
              
              <div style={{ 
                padding: '12px',
                background: '#fefce8',
                border: '1px solid #fef08a',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#a16207',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Building2 size={16} />
                Waiting for administrator to configure Velocity repository
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Worker Configuration (only shown when enabled) */}
      {isEnabled && (
        <div style={{ 
          padding: '16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 600 }}>
            <Clock size={16} style={{ marginRight: '8px', verticalAlign: 'text-bottom' }} />
            Worker Configuration
          </h4>
          
          <div className="form-group">
            <label htmlFor="dsa-poll-interval">Poll Interval</label>
            <select
              id="dsa-poll-interval"
              value={pollIntervalMs}
              onChange={(e) => setPollIntervalMs(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value={60000}>1 minute</option>
              <option value={300000}>5 minutes (default)</option>
              <option value={600000}>10 minutes</option>
              <option value={900000}>15 minutes</option>
              <option value={1800000}>30 minutes</option>
            </select>
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              How often the worker checks for agents to run
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="dsa-min-score">Minimum Eligibility Score</label>
            <input
              id="dsa-min-score"
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={minimumScore}
              onChange={(e) => setMinimumScore(parseFloat(e.target.value))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Agents must score above this threshold (0.0-1.0) to be executed
            </small>
          </div>
          
          <button
            className="btn btn-secondary"
            onClick={handleUpdateConfig}
            disabled={isUpdatingConfig}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            {isUpdatingConfig ? (
              <>
                <RefreshCw size={14} className="spin" style={{ marginRight: '6px' }} />
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </button>
        </div>
      )}
      
      {/* Connected Agents Summary (only when enabled) */}
      {isEnabled && status?.agents && status.agents.length > 0 && (
        <div style={{ 
          marginTop: '16px',
          padding: '16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600 }}>
            <Zap size={16} style={{ marginRight: '8px', verticalAlign: 'text-bottom' }} />
            Connected Agents ({status.agents.length})
          </h4>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {status.agents.map(agent => (
              <span
                key={agent.name}
                style={{
                  padding: '4px 10px',
                  background: agent.status === 'active' ? '#dcfce7' : '#f3f4f6',
                  color: agent.status === 'active' ? '#16a34a' : '#6b7280',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500
                }}
              >
                {agent.displayName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
